import dayjs from 'dayjs';
import BigDecimal from 'js-big-decimal';

import { DEFAULT_DATE_PATTERN } from '@/config/common.config';

export class NumberUtils {
  static prettifyWithRound(
    value: any,
    digits: number = 3,
    separator: string = ',',
    precision: number = 2
  ) {
    try {
      const roundedValue = NumberUtils.roundBigDecimal(value, precision);
      const formattedValue = NumberUtils.prettifyBigDecimal(roundedValue, digits, separator);

      return formattedValue;
    }
    catch {
      return value;
    }
  }

  static roundBigDecimal(value: any, precision: number = 2) {
    try {
      if (!NumberUtils.isNumeric(value)) {
        throw new Error('value is not a number');
      }

      return BigDecimal.round(value, precision, BigDecimal.RoundingModes.HALF_UP);
    }
    catch {
      return value;
    }
  }

  static prettifyBigDecimal(value: any, digits: number = 3, separator: string = ',') {
    try {
      if (!NumberUtils.isNumeric(value)) {
        throw new Error('value is not a number');
      }

      return BigDecimal.getPrettyValue(value, digits, separator);
    }
    catch {
      return value;
    }
  }

  static isNumeric(value: any): value is string | number {
    if (value === null || value === undefined) return false;
    if (typeof value === 'number' && !Number.isNaN(value) && Number.isFinite(value)) return true;

    return typeof value === 'string' && value.length > 0
      && !Number.isNaN(Number(value))
      && Number.isFinite(Number(value));
  }

  static removeExtraZerosAtTheEnd(value: string | number): string {
    const number = Number(value);

    if (NumberUtils.isNumeric(number) && Number.isInteger(number)) {
      return `${number}.00`;
    }

    if (typeof value === 'string') {
      return value.startsWith('0') ? value : parseFloat(value).toString();
    }

    return value.toString();
  }

  static parseNumericInput(value: string): number {
    const sanitized = value.replace(/,/g, '');
    return Number.parseFloat(sanitized);
  }

  static parseIntegerInput(value: string): number {
    const sanitized = value.replace(/,/g, '');
    return parseInt(sanitized, 10);
  }

  static parseFloatWithDecimal(value: string | null): number | null {
    if (value) {
      const twoDecimalPrettified = NumberUtils.prettifyWithRound(value);
      return Number.parseFloat(twoDecimalPrettified);
    }
    return null;
  }

  static formatDate(date: Date): string {
    if (dayjs(date ?? null).isValid()) {
      return dayjs(date).format(DEFAULT_DATE_PATTERN);
    }
    return '';
  }

}
